import type { Point } from "./api";
import type { ElectionFilters } from "./router";

export interface MyBallotSnapshot {
  cycle: string;
  ballot: string;
  displayLabel: string;
  point: Point;
  raceSlugs: string[];
  priorFilters: ElectionFilters;
  priorRace: string | null;
  priorScroll: number;
  finderFocusTarget: "input" | "submit";
}

interface Entry {
  snapshot: MyBallotSnapshot;
  originKey: string;
  /** Number of pushed entries between this entry and originKey. */
  steps: number;
}

function clone(snapshot: MyBallotSnapshot): MyBallotSnapshot {
  return {
    ...snapshot,
    point: { ...snapshot.point },
    raceSlugs: [...snapshot.raceSlugs],
    priorFilters: {
      ...snapshot.priorFilters,
      responses: snapshot.priorFilters.responses
        ? [...snapshot.priorFilters.responses]
        : snapshot.priorFilters.responses,
    },
  };
}

/** Preserve host-page state while adding only the Tool's opaque entry key. */
export function opaqueHistoryState(state: unknown, bscKey: string) {
  const existing =
    state && typeof state === "object" && !Array.isArray(state)
      ? (state as Record<string, unknown>)
      : {};
  return { ...existing, bscKey };
}

/**
 * A document-lifetime store. Nothing here is serialized; browser history
 * carries only the key used to find an entry while this module remains live.
 */
export function createMyBallotStateStore(
  newKey: () => string
) {
  const entries = new Map<string, Entry>();

  return {
    get(key: string): MyBallotSnapshot | null {
      const entry = entries.get(key);
      return entry ? clone(entry.snapshot) : null;
    },

    enter(originKey: string, snapshot: MyBallotSnapshot): { key: string } {
      const key = newKey();
      entries.set(key, {
        snapshot: clone(snapshot),
        originKey,
        steps: 1,
      });
      return { key };
    },

    /** Carry an active result through a Person entry and its Return link. */
    copy(fromKey: string, toKey: string): void {
      const source = entries.get(fromKey);
      if (!source) return;
      entries.set(toKey, {
        snapshot: clone(source.snapshot),
        originKey: source.originKey,
        steps: source.steps + 1,
      });
    },

    /** Delete every descendant so Back/Forward cannot resurrect the result. */
    leave(key: string): {
      originKey: string;
      steps: number;
      snapshot: MyBallotSnapshot;
    } | null {
      const current = entries.get(key);
      if (!current) return null;
      for (const [entryKey, entry] of entries) {
        if (entry.originKey === current.originKey) entries.delete(entryKey);
      }
      return {
        originKey: current.originKey,
        steps: current.steps,
        snapshot: clone(current.snapshot),
      };
    },
  };
}
